import {
  TENANT_BOOTSTRAP_ERROR_CODES,
  TenantBootstrapError,
} from "./bootstrapErrors";
import { createCentralTenantBootstrapRepository } from "./CentralTenantBootstrapRepository";

const REQUIRED_FIREBASE_CONFIG_FIELDS = [
  "apiKey",
  "authDomain",
  "projectId",
  "storageBucket",
  "messagingSenderId",
  "appId",
];

const getTrimmedString = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed || null;
};

const validateFirebaseConfig = (firebaseConfig) => {
  const normalizedFirebaseConfig = {
    apiKey: getTrimmedString(firebaseConfig?.apiKey),
    authDomain: getTrimmedString(firebaseConfig?.authDomain),
    projectId: getTrimmedString(firebaseConfig?.projectId),
    storageBucket: getTrimmedString(firebaseConfig?.storageBucket),
    messagingSenderId: getTrimmedString(firebaseConfig?.messagingSenderId),
    appId: getTrimmedString(firebaseConfig?.appId),
    measurementId: getTrimmedString(firebaseConfig?.measurementId),
  };

  const missingFields = REQUIRED_FIREBASE_CONFIG_FIELDS.filter(
    (field) => !normalizedFirebaseConfig[field]
  );

  if (missingFields.length > 0) {
    throw new TenantBootstrapError(
      TENANT_BOOTSTRAP_ERROR_CODES.TENANT_CONFIG_INVALID,
      "Tenant Firebase config is missing required fields.",
      { missingFields }
    );
  }

  return normalizedFirebaseConfig;
};

export const createTenantBootstrapService = ({ repository } = {}) => {
  const resolvedRepository =
    repository || createCentralTenantBootstrapRepository();

  return {
    async resolveTenant(tenantSlug) {
      let tenantDocument;

      try {
        tenantDocument = await resolvedRepository.getByTenantSlug(tenantSlug);
      } catch (error) {
        throw new TenantBootstrapError(
          TENANT_BOOTSTRAP_ERROR_CODES.CENTRAL_FIRESTORE_UNAVAILABLE,
          "Central Firestore tenant bootstrap lookup failed.",
          { tenantSlug, cause: error }
        );
      }

      if (!tenantDocument) {
        throw new TenantBootstrapError(
          TENANT_BOOTSTRAP_ERROR_CODES.TENANT_NOT_FOUND,
          "Tenant bootstrap document was not found.",
          { tenantSlug }
        );
      }

      const status = getTrimmedString(tenantDocument.status) || "active";
      const clientName =
        getTrimmedString(tenantDocument.clientName) || tenantSlug;
      const firebaseConfig = validateFirebaseConfig(
        tenantDocument.firebaseConfig
      );

      if (status.toLowerCase() !== "active") {
        throw new TenantBootstrapError(
          TENANT_BOOTSTRAP_ERROR_CODES.TENANT_INACTIVE,
          "Tenant is not active.",
          { tenantSlug, status }
        );
      }

      return {
        tenantSlug,
        clientName,
        status,
        firebaseConfig,
        companyLogoUrl:
          getTrimmedString(tenantDocument.companyLogoUrl) ||
          getTrimmedString(tenantDocument.logoUrl) ||
          null,
        bootstrapResolvedAt: new Date().toISOString(),
        compatibilityStatusDefaulted: !tenantDocument.status,
      };
    },
  };
};
